/**
 * useQueryHistory Hook
 * 
 * Sidebar sorgu geçmişi için tüm state ve logic'i yöneten custom hook.
 * HomePage.js'den ayrıştırıldı - tek sorumluluk prensibi.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import useTranslation from '@/hooks/useTranslation';
import { getReadableErrorMessage } from '@/utils/errorHandler';

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL, API } from '@/utils/apiConfig';

/**
 * mapV4ResultFromHistory - Geçmişteki v4 sonucunu UI formatına dönüştürür
 */
export const mapV4ResultFromHistory = (rawResult, queryUsername, t = null) => {
  if (rawResult.opara_core_metrikleri) {
    // v4 format - map to UI format
    
    // v18.5 FIX: Parse follower count from multiple sources with English key fallbacks
    const parseFollowerCount = () => {
      // First try takipci_sayisi (numeric)
      if (rawResult.takipci_sayisi && typeof rawResult.takipci_sayisi === 'number') {
        return rawResult.takipci_sayisi;
      }
      // Then try manuel_girisler.takipci_sayisi (may be string with commas)
      const manuelVal = rawResult.manuel_girisler?.takipci_sayisi;
      if (manuelVal) {
        if (typeof manuelVal === 'number') return manuelVal;
        if (typeof manuelVal === 'string') {
          const cleaned = manuelVal.replace(/,/g, '').replace(/\./g, '').trim();
          const parsed = parseInt(cleaned, 10);
          return isNaN(parsed) ? 0 : parsed;
        }
      }
      // v18.5: English key fallback - user_inputs.follower_count
      const userInputVal = rawResult.user_inputs?.follower_count;
      if (userInputVal) {
        if (typeof userInputVal === 'number') return userInputVal;
        if (typeof userInputVal === 'string') {
          const cleaned = userInputVal.replace(/,/g, '').replace(/\./g, '').trim();
          const parsed = parseInt(cleaned, 10);
          return isNaN(parsed) ? 0 : parsed;
        }
      }
      return 0;
    };
    
    // v31: KALDIRILDI — frontend'in "sepet2 < 3 → Like'lar Gizli" turetmesi handbook 4.3'e AYKIRIYDI
    // (veri=2 gecerli deger uretir, "gizli" DEGIL). Artik backend'in OTORITER bayraklari okunuyor.
    const coreM = rawResult.opara_core_metrikleri || {};
    const validationM = rawResult.validation_status || rawResult.guvenlik_kontrolleri_durumu || {};
    // GERCEK gizli-like: yalniz backend all_likes_hidden=true
    const allLikesHidden = coreM.all_likes_hidden === true;
    // GERCEK yetersiz veri: backend insufficient_data / audit_status / 0-reel sinyali
    const basket1CountM = validationM.basket_1_count ?? validationM.sepet_1_reel_sayisi ?? 0;
    const backendInsufficient = validationM.insufficient_data === true
      || rawResult.audit_status === 'INSUFFICIENT_DATA'
      || basket1CountM === 0;
    const insufficientError = backendInsufficient ? (t ? t('results.insufficient_data') : 'Yetersiz Veri') : null;
    
    return {
      // Basic info
      username: rawResult.username || rawResult.target_profile || queryUsername,
      profile_pic_url: rawResult.profile_pic_url || rawResult.profile?.profile_pic_url,
      follower_count: parseFollowerCount(),
      payment_per_reel: (() => {
        // v18.5: English key fallback
        const val = rawResult.manuel_girisler?.reel_is_birligi_maliyeti_tl 
          || rawResult.manuel_girisler?.reel_is_birligi_maliyeti
          || rawResult.user_inputs?.payment_per_reel;
        if (!val || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace(/,/g, ''));
        return val;
      })(),
      // C1: persist edilen secili para birimi (user_inputs.currency) -> AnalizGecmisi:244 -> AnalysisResults etiketi
      currency: rawResult.user_inputs?.currency || rawResult.manuel_girisler?.currency || null,

      // Core metrics - v18.5: English key fallbacks for new format
      target_reel_views: (() => {
        // Turkish key first, then English fallback
        const val = rawResult.opara_core_metrikleri?.beklenen_reel_izlenme_adet
          || rawResult.opara_core_metrikleri?.expected_views_avg;
        if (!val || val === "N/A" || val === "-") return null;
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const cleaned = val.replace(/,/g, '').trim();
          if (cleaned.endsWith('K')) return parseFloat(cleaned.replace('K', '')) * 1000;
          if (cleaned.endsWith('M')) return parseFloat(cleaned.replace('M', '')) * 1000000;
          return parseFloat(cleaned);
        }
        return null;
      })(),
      target_reel_views_range_min: rawResult.opara_core_metrikleri?.beklenen_izlenme_araligi_min 
        || rawResult.opara_core_metrikleri?.expected_views_min || null,
      target_reel_views_range_max: rawResult.opara_core_metrikleri?.beklenen_izlenme_araligi_max 
        || rawResult.opara_core_metrikleri?.expected_views_max || null,
      target_reel_views_range_display: rawResult.opara_core_metrikleri?.beklenen_izlenme_araligi_display 
        || rawResult.opara_core_metrikleri?.expected_views_display || '-',
      story_view_range: rawResult.opara_core_metrikleri?.beklenen_story_izlenme_araligi 
        || rawResult.opara_core_metrikleri?.story_views_display || null,
      story_view_min: rawResult.opara_core_metrikleri?.beklenen_story_izlenme_min 
        || rawResult.opara_core_metrikleri?.story_views_min || null,
      story_view_max: rawResult.opara_core_metrikleri?.beklenen_story_izlenme_max 
        || rawResult.opara_core_metrikleri?.story_views_max || null,
      story_view_average: rawResult.opara_core_metrikleri?.beklenen_story_izlenme_mid 
        || rawResult.opara_core_metrikleri?.story_views_avg || null,
      link_click_range: rawResult.opara_core_metrikleri?.beklenen_link_tiklama_araligi 
        || rawResult.opara_core_metrikleri?.link_clicks_display || null,
      link_click_min: rawResult.opara_core_metrikleri?.beklenen_link_tiklama_min 
        || rawResult.opara_core_metrikleri?.link_clicks_min || null,
      link_click_max: rawResult.opara_core_metrikleri?.beklenen_link_tiklama_max 
        || rawResult.opara_core_metrikleri?.link_clicks_max || null,
      cost_per_view: (() => {
        const val = rawResult.opara_core_metrikleri?.izlenme_basina_maliyet_tl
          || rawResult.opara_core_metrikleri?.cpv;
        if (!val || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace(/,/g, ''));
        return val;
      })(),
      total_engagement: (() => {
        const val = rawResult.opara_core_metrikleri?.beklenen_is_birligi_etkilesimi_adet
          || rawResult.opara_core_metrikleri?.expected_engagement_avg;
        if (!val || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace(/,/g, ''));
        return val;
      })(),
      // v31: Yalniz GERCEK yetersiz-veri durumunda hata; aksi halde backend gercek degeri gosterilir
      total_engagement_error: insufficientError,
      expected_interaction_rate: (() => {
        const val = rawResult.opara_core_metrikleri?.beklenen_etkilesim_orani_yuzde
          || rawResult.opara_core_metrikleri?.expected_er_percent;
        if (!val || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace('%', '').replace(',', '.'));
        return val;
      })(),
      follower_interaction: (() => {
        const val = rawResult.opara_core_metrikleri?.reel_basina_etkilesim_adet
          || rawResult.opara_core_metrikleri?.engagement_per_reel;
        if (!val || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace(/,/g, ''));
        return val;
      })(),
      // v31: Yalniz GERCEK yetersiz-veri durumunda hata; valid_count=2 → null (gercek deger)
      follower_interaction_error: insufficientError,
      // v31 gate-sinyali: useAnalyzeReel.js:142-143 ile SIMETRIK export — gecmis (history)
      // gorunumu de veri-yetersiz gating'i gorebilsin. Stored validation_status'tan; yeniden-analiz gerekmez.
      is_insufficient_data: backendInsufficient,
      recent_reels_count: validationM.last_90d_reel_count ?? validationM.son_90_gun_reel_sayisi ?? 0,
      all_likes_hidden: allLikesHidden,
      // COMMIT-E1: per-metrik bos-sebep anahtarlari (D2 uretir) -> render sebep-metni
      visibility_reason_key: rawResult.other_metrics?.visibility_reason_key || null,
      engagement_reason_key: rawResult.other_metrics?.engagement_reason_key || null,
      forecast_views_reason_key: rawResult.other_metrics?.forecast_views_reason_key || null,
      forecast_engagement_reason_key: rawResult.other_metrics?.forecast_engagement_reason_key || null,
      engagement_rate: (() => {
        const val = rawResult.opara_core_metrikleri?.takipci_baglilik_orani_yuzde
          || rawResult.opara_core_metrikleri?.loyalty_rate_percent;
        if (val == null || val === "N/A") return null;
        if (typeof val === 'string') return parseFloat(val.replace('%', '').replace(',', '.'));
        return parseFloat(val);
      })(),
      engagement_evaluation: rawResult.opara_core_metrikleri?.baglilik_oranı_derecelendirme 
        || rawResult.opara_core_metrikleri?.loyalty_rating || '-',
      engagement_segment_description: rawResult.opara_core_metrikleri?.segment_description || '',
      visibility_segment_description: rawResult.opara_core_metrikleri?.visibility_segment_description || '',
      
      // Activity metrics - v19.2: Yeni key fallback'ler eklendi
      // DB'de: activity_score, activity_status, activity_summary, activity_description
      velocity_score: rawResult.diger_metrikler?.reel_paylasma_aktifligi_kriter 
        || rawResult.other_metrics?.post_frequency_score 
        || rawResult.other_metrics?.activity_score || 0,
      velocity_evaluation: rawResult.diger_metrikler?.aktiflik_durumu 
        || rawResult.other_metrics?.post_frequency_status 
        || rawResult.other_metrics?.activity_status || '-',
      velocity_explanation: rawResult.diger_metrikler?.aktiflik_son_X_gun_Y_reel 
        || rawResult.other_metrics?.post_frequency_detail 
        || rawResult.other_metrics?.activity_summary || '-',
      velocity_user_explanation: rawResult.diger_metrikler?.aktiflik_aciklamasi 
        || rawResult.other_metrics?.post_frequency_description 
        || rawResult.other_metrics?.activity_description || '-',
      
      // Görünürlük Performansı - v19.2: Yeni key fallback'ler eklendi
      // DB'de: visibility_score, visibility_rating, visibility_color, avg_reel_views
      visibility_performance: rawResult.diger_metrikler?.gorunurluk_performansi_yuzde 
        || rawResult.other_metrics?.visibility_percent 
        || rawResult.other_metrics?.visibility_score || null,
      visibility_evaluation: rawResult.diger_metrikler?.gorunurluk_performansi_degerlendirme 
        || rawResult.other_metrics?.visibility_status 
        || rawResult.other_metrics?.visibility_rating || '-',
      visibility_color: rawResult.diger_metrikler?.gorunurluk_performansi_renk 
        || rawResult.other_metrics?.visibility_color || 'gray',
      visibility_avg_views: rawResult.diger_metrikler?.gorunurluk_ortalama_izlenme 
        || rawResult.other_metrics?.avg_reel_views || null,
      
      // Brand partnerships - v19.2: Yeni key fallback'ler eklendi
      // DB'de: collaboration_count, detected_brands, collaboration_reels
      paid_partnerships_count: (() => {
        // Önce direkt sayıyı dene
        const count = rawResult.diger_metrikler?.ilk_12_reeldeki_is_birligi_sayisi 
          || rawResult.other_metrics?.collaborations_count
          || rawResult.other_metrics?.collaboration_count;
        if (count != null) return count;
        
        // Eğer count yoksa, detected_brands array'inden hesapla
        const brands = rawResult.other_metrics?.detected_brands 
          || rawResult.diger_metrikler?.tespit_edilen_markalar || [];
        if (Array.isArray(brands) && brands.length > 0) {
          return brands.length;
        }
        return 0;
      })(),
      brand_mentions: rawResult.diger_metrikler?.tespit_edilen_markalar 
        || rawResult.other_metrics?.detected_brands || [],
      collaborations_by_reel: rawResult.diger_metrikler?.is_birligi_reelleri 
        || rawResult.other_metrics?.collaboration_reels || [],
      
      // Security checks - v18.5: English key fallbacks
      sepet_1_count: rawResult.guvenlik_kontrolleri_durumu?.sepet_1_reel_sayisi 
        || rawResult.validation_status?.basket_1_count || 0,
      sepet_2_count: rawResult.guvenlik_kontrolleri_durumu?.sepet_2_reel_sayisi 
        || rawResult.validation_status?.basket_2_count || 0,
      sepet_1_status: rawResult.guvenlik_kontrolleri_durumu?.sepet_1_kont_sonucu 
        || rawResult.validation_status?.basket_1_status || '-',
      sepet_2_status: rawResult.guvenlik_kontrolleri_durumu?.sepet_2_kont_sonucu 
        || rawResult.validation_status?.basket_2_status || '-'
    };
  } else {
    // v3 or earlier format
    return {
      ...rawResult,
      brand_mentions: rawResult.brand_mentions || []
    };
  }
};

/**
 * useQueryHistory - Sorgu geçmişi hook'u
 */
export const useQueryHistory = ({
  onHistoryClick,
  formatCurrency
} = {}) => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  
  // History state
  const [queryHistory, setQueryHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);
  
  // Search & Selection state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQueryIds, setSelectedQueryIds] = useState([]);
  
  // Bulk delete modal state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Ref for intersection observer
  const sidebarRef = useRef(null);

  /**
   * fetchQueryHistory - Geçmişi API'den çek
   */
  const fetchQueryHistory = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/user/query-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setQueryHistory(response.data.queries || []);
    } catch (error) {
      console.error('Failed to fetch query history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  /**
   * Auto fetch on mount or when user/token changes
   */
  useEffect(() => {
    if (user && token && !historyFetched) {
      fetchQueryHistory();
      setHistoryFetched(true);
    }
  }, [user, token, historyFetched, fetchQueryHistory]);

  /**
   * handleDeleteQuery - Tek sorgu sil
   */
  const handleDeleteQuery = useCallback(async (queryId) => {
    try {
      await axios.delete(`${API}/user/query-history/${queryId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setQueryHistory(prev => prev.filter(q => q.id !== queryId));
      toast.success(t('notifications.query_deleted'));
    } catch (error) {
      console.error('Failed to delete query:', error);
      toast.error(t('notifications.query_delete_failed'));
    }
  }, [token]);

  /**
   * handleBulkDelete - Toplu silme
   */
  const handleBulkDelete = useCallback(async () => {
    if (selectedQueryIds.length === 0) {
      toast.error(t('notifications.select_queries_to_delete'));
      return;
    }

    try {
      const response = await axios.post(`${API}/user/queries/bulk-delete`, selectedQueryIds, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setQueryHistory(prev => prev.filter(q => !selectedQueryIds.includes(q.id)));
      setSelectedQueryIds([]);
      setShowBulkDeleteConfirm(false);
      toast.success(getReadableErrorMessage({ response: { data: { code: response.data.code, params: response.data.params } } }, t, 'notifications.queries_deleted'));
    } catch (error) {
      console.error('Failed to bulk delete queries:', error);
      toast.error(t('notifications.queries_delete_failed'));
    }
  }, [selectedQueryIds, token]);

  /**
   * toggleQuerySelection - Checkbox toggle
   */
  const toggleQuerySelection = useCallback((queryId) => {
    // HAVUZ savunma: yalniz kendi kaydi secilebilir (backend is_own). Baskasinin kaydi -> sessiz reddet.
    const q = queryHistory.find(x => x.id === queryId);
    if (q && q.is_own === false) return;
    setSelectedQueryIds(prev =>
      prev.includes(queryId)
        ? prev.filter(id => id !== queryId)
        : [...prev, queryId]
    );
  }, [queryHistory]);

  /**
   * selectAllQueries - SAHIP-OLUNAN kayitlari sec (havuz: baskasinin kaydi secilemez)
   */
  const selectAllQueries = useCallback(() => {
    const ownIds = queryHistory.filter(q => q.is_own !== false).map(q => q.id);
    if (ownIds.length > 0 && selectedQueryIds.length === ownIds.length) {
      setSelectedQueryIds([]);
    } else {
      setSelectedQueryIds(ownIds);
    }
  }, [selectedQueryIds.length, queryHistory]);

  /**
   * handleHistoryClick - Geçmişteki sorguyu yükle
   */
  const handleHistoryClick = useCallback((query) => {
    if (query.result_data) {
      // Clean the result data
      const rawResult = { ...query.result_data };
      if (rawResult._id) delete rawResult._id;
      
      // Map to UI format
      const mappedResult = mapV4ResultFromHistory(rawResult, query.username, t);
      
      // Callback to parent
      if (onHistoryClick) {
        onHistoryClick(query, mappedResult);
      }
      
      // Scroll to analysis section
      setTimeout(() => {
        const analysisSection = document.getElementById('influencer-analysis-section');
        if (analysisSection) {
          const yOffset = -100;
          const y = analysisSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [onHistoryClick]);

  /**
   * getQueryHistoryDuration - Plan'a göre geçmiş süresi
   * Pro ve Business planlar için 3 ay, diğerleri için 1 ay
   */
  const getQueryHistoryDuration = useCallback(() => {
    if (!user) return t('history.last_1_month');
    
    // Pro and Business plans get 3 months history
    const proAndBusinessPlans = [
      "pro_monthly", "pro_onetime", "pro",
      "business_monthly", "business_onetime", "business",
      "package_100", "package_250", "package_500", "package_1000" // Credit packages are also pro-level
    ];
    
    if (proAndBusinessPlans.includes(user.plan_type) ||
        proAndBusinessPlans.includes(user.subscription_plan) ||
        user.email?.endsWith("@personaajans.com")) {
      return t('history.last_3_months');
    }
    
    return t('history.last_1_month');
  }, [user, t]);

  /**
   * filteredHistory - Arama sonuçları
   */
  const filteredHistory = queryHistory.filter(query => 
    searchQuery === "" || 
    query.username.toLowerCase().includes(searchQuery.toLowerCase().replace('@', ''))
  );

  /**
   * refreshHistory - Manuel refresh
   */
  const refreshHistory = useCallback(() => {
    fetchQueryHistory();
  }, [fetchQueryHistory]);

  return {
    // History data
    queryHistory,
    filteredHistory,
    loadingHistory,
    
    // Search
    searchQuery,
    setSearchQuery,
    
    // Selection
    selectedQueryIds,
    setSelectedQueryIds,
    toggleQuerySelection,
    selectAllQueries,
    clearSelectedQueries: () => setSelectedQueryIds([]),
    
    // Bulk delete
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    handleBulkDelete,
    deleteSelectedQueries: handleBulkDelete,
    
    // Actions
    handleDeleteQuery,
    handleHistoryClick,
    refreshHistory,
    
    // Helpers
    getQueryHistoryDuration,
    
    // Ref for sidebar
    sidebarRef
  };
};

export default useQueryHistory;
